import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users } from 'lucide-react';
import './CrowdLevelModal.css';

type CrowdLevel = 'low' | 'moderate' | 'high' | 'very high';

interface CrowdLevelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (level: CrowdLevel) => void;
}

const levelMeta: Array<{ id: CrowdLevel; label: string; color: string }> = [
  { id: 'low', label: 'Low', color: '#10b981' },
  { id: 'moderate', label: 'Moderate', color: '#f59e0b' },
  { id: 'high', label: 'High', color: '#ef4444' },
  { id: 'very high', label: 'Packed', color: '#b91c1c' },
];

const CrowdLevelModal: React.FC<CrowdLevelModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [selected, setSelected] = React.useState<CrowdLevel | null>(null);
  const [isFilling, setIsFilling] = React.useState(false);
  const [fillProgress, setFillProgress] = React.useState(0);
  const fillTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = React.useRef<number | null>(null);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [isOpen]);

  const handleRecommend = () => {
    if (!selected) return;
    
    // If already filling, submit immediately
    if (isFilling) {
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      setIsFilling(false);
      setFillProgress(0);
      onSelect(selected);
      return;
    }
  };
  
  // Start fill animation when a crowd level is selected
  React.useEffect(() => {
    if (!selected || !isOpen) {
      return;
    }
    
    // Clear any existing timers first
    if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    
    // Small delay to ensure UI is ready
    const initTimer = setTimeout(() => {
      // Start fill animation
      setIsFilling(true);
      setFillProgress(0);
      startTimeRef.current = Date.now();
      
      // Update progress every 16ms (60fps)
      progressIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Date.now() - startTimeRef.current;
          const progress = Math.min((elapsed / 2000) * 100, 100);
          setFillProgress(progress);
          
          if (progress >= 100) {
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          }
        }
      }, 16);
      
      // Auto-submit after 2 seconds
      fillTimeoutRef.current = setTimeout(() => {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        setIsFilling(false);
        setFillProgress(0);
        onSelect(selected);
      }, 2000);
    }, 50);
    
    return () => {
      clearTimeout(initTimer);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [selected, isOpen, onSelect]);
  
  // Reset when selection is cleared
  React.useEffect(() => {
    if (!selected) {
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    }
  }, [selected]);

  // Cleanup on unmount or close
  React.useEffect(() => {
    return () => {
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  // Reset when modal opens or closes - ensure no preselection
  React.useEffect(() => {
    if (isOpen) {
      // Reset selection when modal opens to ensure no preselection
      setSelected(null);
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    } else {
      // Also reset on close
      setSelected(null);
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="recommendations-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="crowd-modal"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="crowd-modal-header">
            <h2>How busy is it?</h2>
            <button onClick={onClose} className="close-button">
              <X size={24} />
            </button>
          </div>

          <div className="crowd-options">
            {levelMeta.map((level) => (
              <motion.button
                key={level.id}
                className={`crowd-option modern ${selected === level.id ? 'selected' : ''}`}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelected(level.id)}
              >
                <div className={`crowd-icon-circle level-${level.id.replace(' ', '-')}`}>
                  <Users size={22} />
                </div>
                <div className="crowd-option-title compact">{level.label}</div>
              </motion.button>
            ))}
          </div>

          <div className="crowd-actions">
            <button
              className="action-button secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className={`action-button primary ${isFilling ? 'filling' : ''}`}
              disabled={!selected}
              onClick={handleRecommend}
            >
              <span className="button-content">Recommend</span>
              {isFilling && (
                <div 
                  className="fill-progress"
                  style={{ width: `${fillProgress}%` }}
                />
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CrowdLevelModal;


